/*
 * IMPORTS
 */
import React from "react"; // Npm: react.js library.
import _ from "underscore"; // Npm: underscore.js library.
import { DataGrid } from "@mui/x-data-grid"; // Npm: Material UI Data Grid.
import { useQuery } from "@apollo/client"; // Npm: Apollo client.
import { Chip } from "@mui/material";
import { HiArrowUturnDown, HiArrowPath } from "react-icons/hi2"; // Npm: react-icons library.
import { Box, Flex, Text, Button, useBreakpointValue } from "@chakra-ui/react"; // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import DateRangePicker from "components/DateRangePicker";
import DownloadToExcel from "components/DownloadToExcel";
import CustomerReportDropDownOptions from "components/CustomerReportDropDownOptions";
import TableSpinner from "components/TableSpinner";

/*
 * GRAPHS
 */
import CustomerReadQuery from "./__query__/index.customer.read.query";



const formatAsrPercentage = value => {
  if (value === "-" || value === "" || value == null) return "-";

  const numericValue = Number.parseFloat(value);

  return Number.isFinite(numericValue) ? `${numericValue.toFixed(2)}%` : "-";
};


/*
 * OBJECTS
 */
const Index = () => {
  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    id: false,
  });
  const [filterModel, setFilterModel] = React.useState({
    items: [],
    logicOperator: "and",
    quickFilterValues: [],
    quickFilterLogicOperator: "and",
  });
  const _setFilterDebounced = React.useMemo(
    () => _.debounce(setFilterModel, 500),
    [],
  );
  React.useEffect(
    () => () => _setFilterDebounced.cancel(),
    [_setFilterDebounced],
  );
  const [sortModel, setSortModel] = React.useState([]);

  const [dateRange, setDateRange] = React.useState({
    selection: {
      startDate: Object.DateRangeProvider()?.startDate,
      endDate: Object.DateRangeProvider()?.endDate,
      key: "selection",
    },
  });
  const _isFilterApplied = React.useMemo(
    () => filterModel?.items?.some((i) => i.value != void 0 && i.value !== ""),
    [filterModel],
  );
  const _appliedFilter = React.useMemo(
    () => _isFilterApplied ? filterModel : null,
    [_isFilterApplied, filterModel],
  );
  const _customerReadVariables = React.useMemo(() => ({
    skip: paginationModel?.pageSize * paginationModel?.page,
    take: paginationModel?.pageSize,
    filter: _appliedFilter,
    smsStartDate: dateRange?.selection.startDate,
    smsEndDate: dateRange?.selection.endDate,
  }), [_appliedFilter, dateRange?.selection.endDate, dateRange?.selection.startDate, paginationModel?.page, paginationModel?.pageSize]);
  const _isPreviousPageRef = React.useRef(false);
  const _paginationModelRef = React.useRef(paginationModel);

  // Previous pagination uses Apollo cache-first so visited pages do not hit backend again.
  const _QueryCustomerRead = useQuery(CustomerReadQuery, {
    variables: _customerReadVariables,
    fetchPolicy: _isPreviousPageRef.current ?
      "cache-first" :
      Object.React.App.fetchPolicy,
  });

  const _rowCountRef = React.useRef(0);
  const _tableHeaderHeightRef = React.useRef(0);
  const _isCurrentViewMobile =
    useBreakpointValue({ base: true, md: false }, { fallback: "md" }) ?? false;
         
  
  const _HandleRefresh = React.useCallback(() => {
    _QueryCustomerRead.refetch()
  }, [_QueryCustomerRead.refetch])
  const _HandlePaginationModelChange = React.useCallback((__paginationModel) => {
    // Only backward pagination with the same page size should prefer cached rows.
    _isPreviousPageRef.current =
      __paginationModel.page < _paginationModelRef.current.page &&
      __paginationModel.pageSize === _paginationModelRef.current.pageSize;

    _paginationModelRef.current = __paginationModel;
    setPaginationModel(__paginationModel);
  }, []);
  const _HandleColumnVisibilityModelChange = React.useCallback((newModel) =>
    setColumnVisibilityModel(newModel), []);

  // Data assignment.
  const columns = React.useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No.",
        align: "center",
        headerAlign: "center",
        width: 60,
        sortable: false,
        disableColumnMenu: true,
        filterable: true,
      },
      {
        field: "id",
        headerName: "ID",
        align: "center",
        headerAlign: "center",
        filterable: true,
        hide: true,
      },
      {
        field: "Company.displayName",
        headerName: "Company Name",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        flex: 1,
        headerAlign: "center",
        minWidth: 200,
      },
      {
        field: "type",
        headerName: "Customer Type",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        minWidth: 160,
        type: "singleSelect",
        valueOptions: _.without(
          Object.React.App.enums.CUSTOMER_TYPE.enums?.map((i) => i?.key),
          "TERMINATION",
        ),
      },
      {
        field: "totalSms",
        headerName: "Total Sms",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        type: "number",
        flex: 1,
        minWidth: 100,
      },
      {
        field: "smsDelivered",
        headerName: "Sms Delivered",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        type: "number",
        flex: 1,
        minWidth: 130,
      },
      {
        field: "smsFailed",
        headerName: "Sms Failed",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        type: "number",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "smsPending",
        headerName: "Sms Pending",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        type: "number",
        flex: 1,
        minWidth: 130,
      },
      {
        field: "smsUndelivered",
        headerName: "Sms Undelivered",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        type: "number",
        flex: 1,
        minWidth: 140,
      },
      {
        field: "dlrPercentage",
        headerName: "Dlr Percentage",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 130,

        renderCell: ({ value }) => {
          const numeric = parseFloat(value);
          const bg =
            numeric >= 85 ? "#22c55e" : numeric >= 50 ? "#f59e0b" : "#ef4444"; 
          return (
            <Chip
              label={value ?? "-"}
              size="small"
              sx={{
                backgroundColor: `${bg} !important`,
                color: "white !important",
                fontWeight: 500,
                fontSize: "11px",
              }}
            />
          );
        },
      },
      {
        field: "asrPercentage",
        headerName: "ASR%",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 130,

        renderCell: ({ value }) => {
          const numeric = parseFloat(value);
          const bg =
            numeric >= 85 ? "#22c55e" : numeric >= 50 ? "#f59e0b" : "#ef4444";
          return (
            <Chip
              label={value ?? "-"}
              size="small"
              sx={{
                backgroundColor: `${bg} !important`,
                color: "white !important",
                fontWeight: 500,
                fontSize: "11px",
              }}
            />
          );
        },
      },
      {
        field: "action",
        headerName: "Actions",
        editable: false,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        width: 100,
        renderCell: ({ row }) => (
          <CustomerReportDropDownOptions
            customerId={row?.customerId}
            companyName={row["Company.displayName"]}
            type={row?.type}
            currency={row?.currency}
            startDate={dateRange?.selection?.startDate}
            endDate={dateRange?.selection?.endDate}
          />
        ),
      },
    ],
    [dateRange?.selection?.endDate, dateRange?.selection?.startDate],
  );

  const rows = React.useMemo(
    () =>
      _.compact(
        _QueryCustomerRead?.data?.CustomerRead?.map((r, index) =>
          "READ_SUCCESSFUL" === r.status
            ? {
                id: r?.id ?? `customer-report-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
                customerId: r?.id,
                sno: `${index + paginationModel.page * paginationModel.pageSize + 1}.`,
                "Company.displayName": r?.Company?.displayName ?? "-",
                type: r?.type ?? "-",
                totalSms: r?._smsCountForGivenCustomer?.[r?.id]?.smsCount ?? 0,
                smsDelivered:
                  r?._smsCountForGivenCustomer?.[r?.id]?.smsDelivered ?? 0,
                smsFailed:
                  r?._smsCountForGivenCustomer?.[r?.id]?.smsFailed ?? 0,
                smsPending:
                  r?._smsCountForGivenCustomer?.[r?.id]?.smsAccepted ?? 0,
                smsUndelivered:
                  r?._smsCountForGivenCustomer?.[r?.id]?.smsUndelivered ?? 0,
                dlrPercentage: !r?._smsCountForGivenCustomer?.[r?.id]?.smsCount
                  ? "0%"
                  : `${(
                      ((r?._smsCountForGivenCustomer?.[r?.id]?.smsDelivered ||
                        0) /
                        (r?._smsCountForGivenCustomer?.[r?.id]?.smsCount ||
                          0)) *
                      100
                    )?.toFixed(2)}%`,
                asrPercentage: formatAsrPercentage(
                  r?._smsCountForGivenCustomer?.[r?.id]?.asrPercentage,
                ),
                currency: r?.Billing?.currency,
              }
            : null,
        ),
      ),
    [_QueryCustomerRead?.data?.CustomerRead, paginationModel.page, paginationModel.pageSize],
  );
  const _rowCount = _.first(_QueryCustomerRead?.data?.CustomerRead)?._totalCount;
  const rowCount = React.useMemo(() => {
    if (undefined !== _rowCount && null !== _rowCount)
      _rowCountRef.current = _rowCount;

    return _rowCountRef.current;
  }, [_rowCount]);
  const cellsData = React.useMemo(() => _QueryCustomerRead?.data?.CustomerRead?.map(
    (item, __index) =>
      "READ_SUCCESSFUL" === item.status
        ? {
            "S.No.": `${__index + paginationModel.pageSize * paginationModel.page + 1}.`,
            "Company Name": item?.Company?.displayName ?? "-",
            "Open SID":
              _.compact(
                _.flatten(
                  _.pluck(
                    _.flatten(
                      _.pluck(item.CustomerAccount, "Smpp"),
                    ),
                    "sourceAddress",
                  ),
                ),
              )?.join(", ") ?? "-",
            "Total Sms":
              item?._smsCountForGivenCustomer?.[item?.id]?.smsCount ?? "-",
            "Sms Delivered":
              item?._smsCountForGivenCustomer?.[item?.id]?.smsDelivered ?? "-",
            "Sms Failed":
              item?._smsCountForGivenCustomer?.[item?.id]?.smsFailed ?? "-",
            "Sms Pending":
              item?._smsCountForGivenCustomer?.[item?.id]?.smsAccepted ?? "-",
            "Sms Undelivered":
              item?._smsCountForGivenCustomer?.[item?.id]?.smsUndelivered ?? "-",
            "Dlr Percentage": `${
              (
                (item?._smsCountForGivenCustomer?.[item?.id]
                  ?.smsDelivered /
                  item?._smsCountForGivenCustomer?.[item?.id]
                    ?.smsCount) *
                100
              )?.toFixed(2) ?? "-"
            }%`,
            "ASR Percentage": formatAsrPercentage(
              item?._smsCountForGivenCustomer?.[item?.id]?.asrPercentage,
            ),
          }
        : void 0,
  ), [_QueryCustomerRead?.data?.CustomerRead, paginationModel.page, paginationModel.pageSize])
  const headersData = React.useMemo(() => [
    "S.No.",
    "Company Name",
    "Open SID",
    "Total Sms",
    "Sms Delivered",
    "Sms Failed",
    "Sms Pending",
    "Sms Undelivered",
    "Dlr Percentage",
    "ASR Percentage",
  ].map((i) => ({ key: i, label: i })), [])
  const slots = React.useMemo(() => ({
    loadingOverlay: () => (
      <TableSpinner
        isLoading={true}
        isEmpty={false}
        chopHeightFromHundredPercentage={
          _tableHeaderHeightRef?.current?.clientHeight
        }
      />
    ),
    noResultsOverlay: () => (
      <TableSpinner
        isLoading={false}
        isEmpty={true}
        chopHeightFromHundredPercentage={
          _tableHeaderHeightRef?.current?.clientHeight
        }
      />
    ),
    noRowsOverlay: () => (
      <TableSpinner
        isLoading={false}
        isEmpty={true}
        chopHeightFromHundredPercentage={
          _tableHeaderHeightRef?.current?.clientHeight
        }
      />
    ),
  }), [])

  // Return component.
  return (
    <>
      <Flex
        className="reportsCustomer base"
        height="auto"
        minH="max-content"
        overflow="visible"
      >
        <Flex
          display="flex"
          flex="0 0 auto"
          flexDirection="column"
          position="relative"
          gap={_isCurrentViewMobile ? "12px" : "22px"}
          bg="white"
          minH="calc(100vh - 20px)"
          height="auto"
          overflow="visible"
          borderRadius="20px"
          p={_isCurrentViewMobile ? "12px" : "22px"}
        >
          <Flex pb="0px" justify="space-between" alignItems="center" gap="22px">
            <Flex flex={0.5} gap="12px" flexDir="column">
              <Text
                as="h3"
                fontSize={17}
                fontWeight={500}
                display="flex"
                alignItems="center"
                color="brand.500"
                gap="6px"
              >
                <HiArrowUturnDown />
                {_isCurrentViewMobile
                  ? "Filter Rows"
                  : "Filter Rows based on Date."}
              </Text>
              <DateRangePicker
                startDate={dateRange?.selection?.startDate}
                endDate={dateRange?.selection?.endDate}
                w="100%"
                fontSize="13px"
                height="max-content"
                gap="6px"
                setSelectDate={setDateRange}
              />
            </Flex>
            <Flex
              flex={0.5}
              justifyContent="flex-end"
              gap="12px"
              alignItems="center"
            >
              <Button
                borderRadius="12px"
                fontWeight="450"
                maxW="250px"
                backgroundColor="purple.200"
                color="purple"
                _hover={{ bg: "purple.800", color: "white" }}
                onClick={_HandleRefresh}
                isDisabled={_QueryCustomerRead?.loading}
                leftIcon={<HiArrowPath />}
              >
                Refresh
              </Button>
              <DownloadToExcel
                cellsData={cellsData}
                headersData={headersData}
              />
            </Flex>
          </Flex>
          <Box
            w="100%"
            height="calc(100vh - 150px)"
            minH="520px"
            flexShrink={0}
            overflow="hidden"
          >
            <DataGrid
              rows={rows}
              columns={columns}
              rowHeight={48}
              rowCount={rowCount}
              paginationMode="server"
              filterMode="server"
              sortingMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={_HandlePaginationModelChange}
              filterModel={filterModel}
              onFilterModelChange={_setFilterDebounced}
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              pageSizeOptions={[10, 30, 50, 100]}
              slots={slots}
              disableRowSelectionOnClick
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={_HandleColumnVisibilityModelChange}
              loading={_QueryCustomerRead?.loading}
            />
          </Box>
        </Flex>
      </Flex>
    </>
  );
};

/*
 * EXPORTS
 */
export default React.memo(Index);
